<script setup>
	import { computed, onBeforeUnmount, onMounted, reactive, watch } from "vue";
	import { executeServiceCall, getValidationErrorsFromResponse, selfServiceService } from "@/services";
	import TestimonialCarousel from "@/components/TestimonialCarousel.vue";

	const testimonials = [
		{
			author: "Geoff A",
			quote: "Chad is very talented technically, and he brings a wealth of experience and ability to the projects he's involved in. He also has a lot to offer in terms of project management — he is an effective communicator and has experience with a variety of software development methodologies. Finally, and most importantly, Chad is a phenomenal mentor and teacher.",
		},
		{
			author: "Joshua L",
			quote: "Chad is one of those rare individuals whose vision extends beyond just the project at hand. He sees the big picture, both technically, managerially, and in relationships. He is very talented when it comes to technical matters, and also knows how to train and mentor individuals to help them reach their maximum potential.",
		},
		{
			author: "Raul G",
			quote: "We hired Chad for a quick turnaround (3 weeks!) SaaS project and he delivered, exceptionally. Our team and, more importantly, the customers felt the demo prototype was professionally done and exceeded expectations. Chad was extremely responsive, knowledgeable and great to work with.",
		},
		{
			author: "Raj S",
			quote: "In a matter of weeks Chad assumed responsibility for the majority of the user interface. Throughout the project Chad offered fresh options and new ideas allowing us to leverage the latest technology capabilities. In addition to development, Chad took a leadership role in defining our build process and policies.",
		},
		{
			author: "Josh S",
			quote: "Chad is easily the smartest and most innovative architect and engineer I have worked with. Rarely do you come across someone with his breadth and command of technical knowledge. He fundamentally understands how to build robust systems that provide value to his clients. He is also a great communicator.",
		},
		{
			author: "Larry M",
			quote: "Chad is a remarkable architect and developer. He was able to deliver on the project, despite the technical and management challenges. With constant change in technical direction, he was able to adapt well. I hope to work with Chad again in the future.",
		},
		{
			author: "AD A",
			quote: "We hired Chad to help us migrate a number of PHP-based applications to .NET as part of a migration off an old intranet. Without existing documentation, Chad was able to work with business users and the existing code base to re-write three applications from the ground up in a matter of weeks.",
		},
		{
			author: "DJ W",
			quote: "Chad Greer is one of the top Microsoft Software Solution Consultants in the market. He has great depth in architecting and developing custom solutions using an Agile approach to solve complex business problems. He has a natural ability to apply his past experiences to new challenges.",
		},
		{
			author: "Tor N",
			quote: "Chad is a technically gifted individual, with an eye for detail, and operates well as part of a team in respect of innovative and creative approaches to design, development and implementation. He has an impeccable work ethic and is one of the most innovative software architects I've known.",
		},
		{
			author: "Matt B",
			quote: "Chad was my counterpart in defining and developing our company's software, including the database, data operations, the presentation software and the overarching architecture. He understands how to write well-formed and maintainable software, but he was a shining star because he understood how to craft an enterprise solution.",
		},
		{
			author: "Chad S",
			quote: "Chad is without a doubt one of the most innovative software architects I've known and a very powerful technical leader. He has a talent for inspiring passion in others. He is very intelligent, has an impeccable work ethic and does his homework. I highly recommend Chad Greer for any technical leadership position.",
		},
	];

	const pipelineSteps = [
		{
			stage: "01",
			status: "scoped",
			title: "Find the real bottleneck",
			description: "Before we write a line of code, we sit with your team through interviews, a workshop, and some shadowing to pin down the one operation that's actually costing you — and the ROI of fixing it.",
		},
		{
			stage: "02",
			status: "shipped",
			title: "Build one focused tool",
			description: "A small, standalone internal app that solves that single problem and makes the work visible and measurable. No scope creep, no software that bleeds into an amorphous blob.",
		},
		{
			stage: "03",
			status: "guaranteed",
			title: "Guaranteed, and yours to steer",
			description: "You set the priorities every week and can stop us the moment we're off track. After sign-off, we guarantee the result for 90 days — if something we agreed was done breaks, we fix it free.",
		},
	];

	const healthChecks = [
		"Every step tracked and owned",
		"Bottlenecks measured, not guessed",
		"One source of truth, in plain sight",
	];

	const data = reactive({
		requestMoreInfo: {
			contactInfo: {
				emailAddress: "",
				name: "",
				text: "",
			},
			isError: false,
			isProcessing: false,
			isShowing: false,
			isSuccess: false,
			topic: "",
			validationErrors: [],
		},
		manualStepsDisplayed: 40,
		visibilityDisplayed: 0,
	});

	const manualStepsStart = 40;
	const manualStepsEnd = 6;
	const visibilityStart = 0;
	const visibilityEnd = 100;
	const animationDurationMilliseconds = 1800;
	const startHoldMilliseconds = 500;
	const replayPeriodMilliseconds = 7000;

	let metricAnimationFrame = null;
	let metricReplayTimer = null;

	const requestMoreInfoContent = {
		General: {
			title: "Talk through your situation",
			description: "Tell us a little about what's going on and we'll get back to you shortly.",
		},
		GetStarted: {
			title: "Let's get started",
			description: "Tell us a bit about your project and we'll reach out to get things moving.",
		},
	};

	const activeRequestMoreInfoContent = computed(() => requestMoreInfoContent[data.requestMoreInfo.topic] || requestMoreInfoContent.General);

	function requestMoreInfo(topic) {
		data.requestMoreInfo.topic = topic;
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.isSuccess = false;
		data.requestMoreInfo.isProcessing = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.isShowing = true;
	}

	function closeRequestMoreInfo() {
		data.requestMoreInfo.isShowing = false;
	}

	async function submitRequestForMoreInfo() {
		data.requestMoreInfo.isProcessing = true;
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.contactInfo.phoneNumber = "n/a";
		data.requestMoreInfo.contactInfo.recaptchaResponse = "skip";

		await executeServiceCall(() => selfServiceService.public.submitContactRequest(data.requestMoreInfo.contactInfo))
			.then(() => {
				data.requestMoreInfo.isSuccess = true;
			})
			.catch(error => {
				if (error?.response?.status === 400) {
					data.requestMoreInfo.validationErrors = getValidationErrorsFromResponse(error.response).map(validationMessage =>
						validationMessage === "Your E-mail Address is required."
							? "Your E-mail Address or Phone Number is required."
							: validationMessage
					);
				} else {
					console.error(error);
					data.requestMoreInfo.isError = true;
				}
			})
			.finally(() => {
				data.requestMoreInfo.isProcessing = false;
			});
	}

	function handleModalKeydown(event) {
		if (event.key === "Escape") {
			closeRequestMoreInfo();
		}
	}

	function runMetricsAnimation() {
		if (metricAnimationFrame) {
			cancelAnimationFrame(metricAnimationFrame);
		}

		const startTimestamp = performance.now();

		function stepMetrics(currentTimestamp) {
			const elapsed = currentTimestamp - startTimestamp - startHoldMilliseconds;
			const progress = elapsed <= 0 ? 0 : Math.min(elapsed / animationDurationMilliseconds, 1);
			const eased = 1 - Math.pow(1 - progress, 3);

			data.manualStepsDisplayed = Math.round(manualStepsStart + (manualStepsEnd - manualStepsStart) * eased);
			data.visibilityDisplayed = visibilityStart + (visibilityEnd - visibilityStart) * eased;

			if (progress < 1) {
				metricAnimationFrame = requestAnimationFrame(stepMetrics);
			}
		}

		metricAnimationFrame = requestAnimationFrame(stepMetrics);
	}

	function startMetricsAnimation() {
		const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		if (prefersReducedMotion) {
			data.manualStepsDisplayed = manualStepsEnd;
			data.visibilityDisplayed = visibilityEnd;
			return;
		}

		runMetricsAnimation();

		metricReplayTimer = setInterval(() => {
			data.manualStepsDisplayed = manualStepsStart;
			data.visibilityDisplayed = visibilityStart;
			runMetricsAnimation();
		}, replayPeriodMilliseconds);
	}

	watch(() => data.requestMoreInfo.isShowing, (isShowing) => {
		if (isShowing) {
			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			document.documentElement.style.overflow = "hidden";
			document.body.style.overflow = "hidden";

			if (scrollbarWidth > 0) {
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			}

			window.addEventListener("keydown", handleModalKeydown);
		} else {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			window.removeEventListener("keydown", handleModalKeydown);
		}
	});

	onMounted(() => {
		startMetricsAnimation();
	});

	onBeforeUnmount(() => {
		if (metricAnimationFrame) {
			cancelAnimationFrame(metricAnimationFrame);
		}

		if (metricReplayTimer) {
			clearInterval(metricReplayTimer);
		}

		document.documentElement.style.overflow = "";
		document.body.style.overflow = "";
		document.body.style.paddingRight = "";
		window.removeEventListener("keydown", handleModalKeydown);
	});
</script>

<template>
	<div class="home">
		<section class="hero">
			<div class="hero-inner">
				<div class="hero-copy">
					<span class="offer-pill">50% off your first 6 weeks</span>
					<h1 class="hero-title">Your worst bottleneck is the one you can't see.</h1>
					<p class="hero-lead">
						We build a small, focused internal tool that pulls that operation into the open —
						visible, measurable, and finally easy to run. Most teams call us after the first
						attempt missed: a stalled internal build, the wrong contractor, or an AI prototype nobody can maintain.
					</p>
					<div class="hero-actions">
						<button type="button" class="btn btn-primary" @click="requestMoreInfo('GetStarted')">Get Started</button>
						<RouterLink :to="{ name: 'how-we-work' }" class="btn btn-ghost">How we work</RouterLink>
					</div>
					<p class="hero-note">You stay in control the whole way — and we guarantee the result for 90 days.</p>
				</div>

				<div class="health-panel">
					<div class="health-header">
						<span class="health-title">Operations dashboard</span>
						<span class="health-badge">live</span>
					</div>
					<div class="health-metrics">
						<div class="metric">
							<span class="metric-label">Manual steps</span>
							<span class="metric-value metric-value-down">{{ data.manualStepsDisplayed.toLocaleString() }}</span>
							<span class="metric-trend">↓ down 85%</span>
						</div>
						<div class="metric">
							<span class="metric-label">Visibility</span>
							<span class="metric-value metric-value-up">{{ data.visibilityDisplayed.toFixed(0) }}%</span>
							<div class="metric-bar"><span class="metric-bar-fill" :style="{ width: `${data.visibilityDisplayed}%` }"></span></div>
						</div>
					</div>
					<ul class="health-checks">
						<li v-for="healthCheck in healthChecks" :key="healthCheck" class="health-check">
							<span class="health-check-icon" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							{{ healthCheck }}
						</li>
					</ul>
				</div>
			</div>
		</section>

		<section class="pipeline">
			<div class="pipeline-inner">
				<header class="block-header">
					<span class="block-eyebrow">How we work</span>
					<h2 class="block-heading">Clarity first. Code second.</h2>
				</header>
				<div class="pipeline-grid">
					<article
						v-for="(pipelineStep, pipelineStepIndex) in pipelineSteps"
						:key="pipelineStep.stage"
						class="pipeline-card"
					>
						<span v-if="pipelineStepIndex < pipelineSteps.length - 1" class="pipeline-connector" aria-hidden="true"></span>
						<div class="pipeline-stage">{{ pipelineStep.stage }}</div>
						<h3 class="pipeline-title">{{ pipelineStep.title }}</h3>
						<p class="pipeline-description">{{ pipelineStep.description }}</p>
						<div class="pipeline-pass"><span class="pipeline-pass-dot"></span> {{ pipelineStep.status }}</div>
					</article>
				</div>
			</div>
		</section>

		<section class="cta">
			<div class="cta-inner">
				<div class="cta-card">
					<h2 class="cta-title">Let's find out what's actually slowing you down</h2>
					<p class="cta-lead">
						Tell us what's broken. We'll dig in, tell you straight whether a tool is even worth
						building, and show you the ROI before any code gets written. You stay in control the whole way.
					</p>
					<div class="cta-actions">
						<button type="button" class="btn btn-primary btn-on-blue" @click="requestMoreInfo('GetStarted')">Get Started</button>
						<button type="button" class="btn btn-ghost-light" @click="requestMoreInfo('General')">Talk through your situation</button>
					</div>
					<p class="cta-offer">New clients get <strong>50% off the first 6 weeks.</strong></p>
				</div>
			</div>
		</section>

		<section class="testimonials">
			<div class="testimonials-inner">
				<header class="block-header">
					<span class="block-eyebrow">What clients say</span>
					<h2 class="block-heading">In their own words</h2>
				</header>

				<div class="testimonials-carousel">
					<TestimonialCarousel :items="testimonials">
						<template #default="{ item }">
							<figure class="testimonial-card">
								<div class="testimonial-rating" aria-hidden="true">★★★★★</div>
								<blockquote class="testimonial-quote">{{ item.quote }}</blockquote>
								<figcaption class="testimonial-author">
									<span class="testimonial-avatar" aria-hidden="true">{{ item.author.charAt(0) }}</span>
									{{ item.author }}
								</figcaption>
							</figure>
						</template>
					</TestimonialCarousel>
				</div>
			</div>
		</section>

		<transition name="modal-fade">
			<div v-if="data.requestMoreInfo.isShowing" class="modal-overlay" @click.self="closeRequestMoreInfo">
				<div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="requestMoreInfoTitle" @click.stop>
					<button type="button" class="modal-close" aria-label="Close" @click="closeRequestMoreInfo"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
					<div class="modal-scroll">
						<template v-if="!data.requestMoreInfo.isSuccess">
							<span class="modal-badge">Free · No commitment</span>
							<h2 id="requestMoreInfoTitle" class="modal-title">{{ activeRequestMoreInfoContent.title }}</h2>
							<p class="modal-description">{{ activeRequestMoreInfoContent.description }}</p>
							<div class="modal-field">
								<label for="requestMoreInfoName">Name</label>
								<input id="requestMoreInfoName" v-model="data.requestMoreInfo.contactInfo.name" type="text" autocomplete="name" :disabled="data.requestMoreInfo.isProcessing" />
							</div>
							<div class="modal-field">
								<label for="requestMoreInfoEmail">Email or phone number</label>
								<input id="requestMoreInfoEmail" v-model="data.requestMoreInfo.contactInfo.emailAddress" type="text" autocomplete="email" :disabled="data.requestMoreInfo.isProcessing" />
							</div>
							<div class="modal-field">
								<label for="requestMoreInfoText">What's slowing you down right now?</label>
								<textarea id="requestMoreInfoText" v-model="data.requestMoreInfo.contactInfo.text" rows="4" :disabled="data.requestMoreInfo.isProcessing"></textarea>
							</div>
							<div v-if="data.requestMoreInfo.validationErrors.length > 0" class="modal-feedback modal-feedback-error" role="alert">
								<p class="modal-feedback-title">Please fix the following:</p>
								<ul class="modal-feedback-list">
									<li v-for="(validationError, validationErrorIndex) in data.requestMoreInfo.validationErrors" :key="validationErrorIndex">{{ validationError }}</li>
								</ul>
							</div>
							<p v-else-if="data.requestMoreInfo.isError" class="modal-feedback modal-feedback-error" role="alert">Something went wrong sending your message. Please try again.</p>
							<button type="button" class="btn btn-primary btn-block" :disabled="data.requestMoreInfo.isProcessing" @click="submitRequestForMoreInfo"><span v-if="data.requestMoreInfo.isProcessing" class="btn-spinner" aria-hidden="true"></span>{{ data.requestMoreInfo.isProcessing ? "Sending…" : "Send message" }}</button>
						</template>
						<div v-else class="modal-success">
							<span class="success-check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							<h2 class="modal-title">Message received</h2>
							<p class="modal-description">Thanks for reaching out — we'll be in touch with you shortly.</p>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<style scoped>
	.home {
		--bg: #f5f9fd;
		--surface: #ffffff;
		--surface-muted: #eef4fb;
		--border: #e2ecf6;
		--border-strong: #cddcec;
		--navy: #0f2742;
		--ink: #16314f;
		--ink-soft: #4a5d75;
		--ink-muted: #7589a1;
		--sky: #0ea5e9;
		--sky-deep: #0284c7;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dceafe;

		font-family: "Plus Jakarta Sans", "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
		color: var(--ink);
		background: var(--bg);
		--carousel-accent: var(--blue);
		--carousel-dot: #bdd2e8;
		overflow-x: hidden;
	}

	.hero {
		padding: clamp(3.5rem, 8vw, 6.5rem) 1.5rem clamp(3rem, 6vw, 5rem);
	}

	.hero-inner {
		max-width: 1180px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: 1.05fr 0.95fr;
		gap: clamp(2rem, 5vw, 4rem);
		align-items: center;
	}

	.offer-pill {
		display: inline-block;
		padding: 0.5rem 1.15rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.95rem;
		font-weight: 700;
		margin-bottom: 1.5rem;
	}

	.hero-title {
		font-size: clamp(2.4rem, 4.5vw, 4rem);
		line-height: 1.05;
		font-weight: 800;
		letter-spacing: -0.025em;
		color: var(--navy);
		margin: 0 0 1.5rem;
	}

	.hero-lead {
		font-size: clamp(1.1rem, 1.4vw, 1.28rem);
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0 0 2.25rem;
		max-width: 52ch;
	}

	.hero-actions {
		display: flex;
		gap: 1rem;
		flex-wrap: wrap;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.9rem;
		border-radius: 12px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 700;
		text-decoration: none;
		cursor: pointer;
		border: 1px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 28px -14px rgba(37, 99, 235, 0.7);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 34px -16px rgba(37, 99, 235, 0.75);
	}

	.btn-primary:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 2px;
	}

	.btn-ghost {
		background: var(--surface);
		color: var(--ink);
		border-color: var(--border-strong);
	}

	.btn-ghost:hover {
		transform: translateY(-2px);
		border-color: var(--blue);
		color: var(--blue-deep);
	}

	.btn-ghost:focus-visible {
		outline: 3px solid rgba(15, 39, 66, 0.25);
		outline-offset: 2px;
	}

	.btn-on-blue {
		background: #ffffff;
		color: var(--blue-deep);
		box-shadow: 0 14px 30px -14px rgba(0, 0, 0, 0.5);
	}

	.btn-on-blue:hover {
		color: var(--blue-deep);
		transform: translateY(-2px);
		background: #f1f6ff;
		box-shadow: 0 20px 36px -16px rgba(0, 0, 0, 0.55);
	}

	.btn-ghost-light {
		background: transparent;
		color: #ffffff;
		border-color: rgba(255, 255, 255, 0.45);
	}

	.btn-ghost-light:hover {
		transform: translateY(-2px);
		background: rgba(255, 255, 255, 0.12);
		border-color: #ffffff;
	}

	.btn-ghost-light:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.6);
		outline-offset: 2px;
	}

	.hero-note {
		margin: 1.5rem 0 0;
		font-size: 0.95rem;
		color: var(--ink-muted);
	}

	.health-panel {
		padding: clamp(1.75rem, 3vw, 2.25rem);
		border-radius: 20px;
		background: var(--surface);
		border: 1px solid var(--border);
		box-shadow: 0 34px 64px -38px rgba(15, 39, 66, 0.5);
	}

	.health-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 1.5rem;
	}

	.health-title {
		font-size: 1.1rem;
		font-weight: 700;
		color: var(--navy);
	}

	.health-badge {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		padding: 0.3rem 0.7rem;
		border-radius: 999px;
		background: rgba(14, 165, 233, 0.12);
		color: var(--sky-deep);
		font-size: 0.78rem;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.06em;
	}

	.health-metrics {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1rem;
		margin-bottom: 1.5rem;
	}

	.metric {
		padding: 1.1rem 1.2rem;
		border-radius: 14px;
		background: var(--surface-muted);
		border: 1px solid var(--border);
	}

	.metric-label {
		display: block;
		font-size: 0.85rem;
		color: var(--ink-muted);
		margin-bottom: 0.5rem;
	}

	.metric-value {
		display: block;
		font-size: clamp(1.8rem, 3vw, 2.4rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		line-height: 1;
	}

	.metric-value-down { color: var(--blue-deep); }
	.metric-value-up { color: var(--sky-deep); }

	.metric-trend {
		display: block;
		margin-top: 0.5rem;
		font-size: 0.82rem;
		font-weight: 600;
		color: var(--ink-muted);
	}

	.metric-bar {
		margin-top: 0.7rem;
		height: 8px;
		border-radius: 999px;
		background: #d4e3f3;
		overflow: hidden;
	}

	.metric-bar-fill {
		display: block;
		height: 100%;
		border-radius: 999px;
		background: linear-gradient(90deg, var(--sky), var(--blue));
		transition: width 0.2s ease;
	}

	.health-checks {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 0.7rem;
	}

	.health-check {
		display: flex;
		align-items: center;
		gap: 0.7rem;
		font-size: 1rem;
		font-weight: 500;
		color: var(--ink);
	}

	.health-check-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		flex-shrink: 0;
	}

	.health-check-icon svg {
		width: 14px;
		height: 14px;
	}

	.pipeline {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.pipeline-inner {
		max-width: 1140px;
		margin: 0 auto;
	}

	.block-header {
		text-align: center;
		margin-bottom: 2.75rem;
	}

	.block-eyebrow {
		display: inline-block;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.7rem;
	}

	.block-heading {
		font-size: clamp(1.85rem, 3vw, 2.7rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0;
	}

	.pipeline-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1.5rem;
	}

	.pipeline-card {
		position: relative;
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 2rem 1.75rem;
		border: 1px solid var(--border);
		border-radius: 16px;
		background: var(--bg);
		transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
	}

	.pipeline-card:hover {
		transform: translateY(-4px);
		border-color: var(--blue);
		box-shadow: 0 24px 44px -30px rgba(15, 39, 66, 0.5);
	}

	.pipeline-connector {
		position: absolute;
		top: 2.85rem;
		right: -0.95rem;
		width: 1.9rem;
		height: 2px;
		background: var(--border-strong);
		z-index: 1;
	}

	.pipeline-stage {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 46px;
		height: 46px;
		border-radius: 12px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 1.1rem;
		font-weight: 800;
		margin-bottom: 1.25rem;
	}

	.pipeline-title {
		font-size: 1.3rem;
		font-weight: 700;
		color: var(--navy);
		margin: 0 0 0.7rem;
	}

	.pipeline-description {
		font-size: 1.02rem;
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0 0 1.5rem;
	}

	.pipeline-pass {
		display: inline-flex;
		align-items: center;
		gap: 0.45rem;
		margin-top: auto;
		align-self: flex-start;
		font-size: 0.85rem;
		font-weight: 700;
		color: var(--sky-deep);
	}

	.pipeline-pass-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: var(--sky);
	}

	.cta {
		padding: clamp(3.5rem, 6vw, 5.5rem) 1.5rem;
		background: var(--bg);
	}

	.cta-inner {
		max-width: 1000px;
		margin: 0 auto;
	}

	.cta-card {
		padding: clamp(3rem, 5vw, 4.5rem) clamp(1.75rem, 4vw, 4rem);
		border-radius: 24px;
		background: linear-gradient(135deg, var(--blue) 0%, var(--sky-deep) 100%);
		color: #ffffff;
		text-align: center;
		box-shadow: 0 34px 64px -36px rgba(37, 99, 235, 0.7);
	}

	.cta-title {
		font-size: clamp(1.9rem, 3.5vw, 3rem);
		font-weight: 800;
		line-height: 1.1;
		letter-spacing: -0.02em;
		margin: 0 auto 1.5rem;
		max-width: 20ch;
		color: #ffffff;
	}

	.cta-lead {
		font-size: clamp(1.1rem, 1.4vw, 1.28rem);
		line-height: 1.6;
		color: rgba(255, 255, 255, 0.92);
		max-width: 56ch;
		margin: 0 auto 2.5rem;
	}

	.cta-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: 1.75rem;
	}

	.cta-offer {
		font-size: 1.05rem;
		color: rgba(255, 255, 255, 0.92);
		margin: 0;
	}

	.cta-offer strong { color: #ffffff; }

	.testimonials {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
	}

	.testimonials-inner {
		max-width: 1240px;
		margin: 0 auto;
	}

	.testimonials-carousel {
		margin-top: 1rem;
	}

	.testimonial-card {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 2rem 1.85rem;
		border: 1px solid var(--border);
		border-radius: 18px;
		background: var(--bg);
		margin: 0;
		box-sizing: border-box;
	}

	.testimonial-rating {
		color: var(--sky);
		font-size: 1.05rem;
		letter-spacing: 0.15em;
		margin-bottom: 1rem;
	}

	.testimonial-quote {
		flex: 1;
		font-size: 1.02rem;
		line-height: 1.7;
		color: var(--ink-soft);
		margin: 0 0 1.5rem;
	}

	.testimonial-author {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		font-size: 1rem;
		font-weight: 700;
		color: var(--navy);
	}

	.testimonial-avatar {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 1rem;
		font-weight: 800;
	}

	.modal-overlay {
		--bg: #f5f9fd;
		--surface: #ffffff;
		--surface-muted: #eef4fb;
		--border: #e2ecf6;
		--border-strong: #cddcec;
		--navy: #0f2742;
		--ink: #16314f;
		--ink-soft: #4a5d75;
		--ink-muted: #7589a1;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dceafe;
		position: fixed;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		background: rgba(15, 39, 66, 0.5);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		cursor: pointer;
		z-index: 100000;
		pointer-events: auto;
	}

	.modal-dialog {
		position: relative;
		pointer-events: auto;
		width: 100%;
		max-width: 500px;
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow: hidden;
		border-radius: 20px;
		background: var(--surface);
		border: 1px solid var(--border);
		box-shadow: 0 40px 90px -30px rgba(15, 39, 66, 0.55);
		cursor: default;
	}

	.modal-scroll {
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		padding: clamp(2.25rem, 4vw, 2.75rem) clamp(1.75rem, 3vw, 2.25rem);
	}

	.modal-close {
		position: absolute;
		top: 1rem;
		right: 1.1rem;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.9rem;
		line-height: 1;
		border-radius: 10px;
		border: 1px solid var(--border);
		background: var(--surface-muted);
		color: var(--ink-muted);
		cursor: pointer;
		transition: background 0.2s ease, color 0.2s ease;
		z-index: 1;
	}

	.modal-close:hover {
		background: var(--blue-soft);
		color: var(--blue-deep);
	}

	.modal-close svg {
		width: 18px;
		height: 18px;
	}

	.modal-badge {
		display: inline-block;
		padding: 0.4rem 0.9rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.82rem;
		font-weight: 700;
		letter-spacing: 0.04em;
		margin-bottom: 0.9rem;
	}

	.btn-block {
		width: 100%;
	}

	.modal-title {
		font-size: 1.7rem;
		font-weight: 800;
		margin: 0 0 0.5rem;
		color: var(--navy);
	}

	.modal-description {
		font-size: 1rem;
		line-height: 1.6;
		color: var(--ink-soft);
		margin: 0 0 1.75rem;
	}

	.modal-field {
		margin-bottom: 1.1rem;
		text-align: left;
	}

	.modal-field label {
		display: block;
		font-size: 0.95rem;
		font-weight: 600;
		color: var(--ink);
		margin-bottom: 0.45rem;
	}

	.modal-field input,
	.modal-field textarea {
		width: 100%;
		padding: 0.8rem 0.95rem;
		border-radius: 10px;
		border: 1px solid var(--border-strong);
		background: var(--bg);
		font-family: inherit;
		font-size: 1rem;
		color: var(--ink);
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
		box-sizing: border-box;
	}

	.modal-field textarea { resize: vertical; }

	.modal-field input:focus,
	.modal-field textarea:focus {
		outline: none;
		background: var(--surface);
		border-color: var(--blue);
		box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
	}

	.modal-field input:disabled,
	.modal-field textarea:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.modal-actions {
		display: flex;
		gap: 0.75rem;
		justify-content: flex-end;
		margin-top: 1.6rem;
	}

	.btn:disabled {
		opacity: 0.65;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	.btn-spinner {
		width: 1.05em;
		height: 1.05em;
		flex-shrink: 0;
		margin-right: 0.55rem;
		border: 2px solid rgba(255, 255, 255, 0.45);
		border-top-color: #ffffff;
		border-radius: 50%;
		-webkit-animation: btn-spin 0.7s linear infinite;
		animation: btn-spin 0.7s linear infinite;
	}

	@-webkit-keyframes btn-spin {
		to { -webkit-transform: rotate(360deg); }
	}

	@keyframes btn-spin {
		to { transform: rotate(360deg); }
	}

	.modal-success {
		text-align: center;
		padding: 0.75rem 0.25rem 0.5rem;
	}

	.success-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background: #dcfce7;
		color: #16a34a;
		margin-bottom: 1.35rem;
	}

	.success-check svg {
		width: 36px;
		height: 36px;
	}

	.modal-feedback {
		margin: 0 0 1.2rem;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		font-size: 0.98rem;
		font-weight: 500;
		text-align: left;
	}

	.modal-feedback-error {
		background: #fbe9ec;
		color: #9c1f2e;
	}

	.modal-feedback-title {
		margin: 0 0 0.4rem;
		font-weight: 700;
	}

	.modal-feedback-list {
		margin: 0;
		padding-left: 1.2rem;
	}

	.modal-feedback-list li {
		margin-bottom: 0.2rem;
	}

	.modal-fade-enter-active,
	.modal-fade-leave-active { transition: opacity 0.25s ease; }

	.modal-fade-enter-active .modal-dialog,
	.modal-fade-leave-active .modal-dialog { transition: transform 0.25s ease, opacity 0.25s ease; }

	.modal-fade-enter-from,
	.modal-fade-leave-to { opacity: 0; }

	.modal-fade-enter-from .modal-dialog,
	.modal-fade-leave-to .modal-dialog { transform: translateY(14px) scale(0.98); opacity: 0; }

	@media (max-width: 900px) {
		.hero-inner { grid-template-columns: 1fr; }

		.pipeline-grid { grid-template-columns: 1fr; }

		.pipeline-connector { display: none; }
	}

	@media (max-width: 640px) {
		.hero-actions,
		.cta-actions { flex-direction: column; align-items: stretch; }

		.btn { width: 100%; }

		.health-metrics { grid-template-columns: 1fr; }

		.modal-actions { flex-direction: column-reverse; }

		.modal-actions .btn { width: 100%; }
	}
</style>